import React from 'react';
import { Link } from 'react-router-dom';
import { buttonReset, color, font, radius, shadow, space, visuallyHidden, weight } from '../theme';
import Icon from './Icon';

interface StatTileProps {
  value: React.ReactNode;
  label: string;
  // Clickable tiles sort or filter the list below (see "Stat tiles" in CLAUDE.md)
  onClick?: () => void;
  // Or the tile opens another page (its label turns blue, with a chevron)
  to?: string;
  state?: unknown;
  selected?: boolean;
  // What the selected tile is doing to the list, shown next to its value
  indicator?: 'desc' | 'asc' | 'filter';
  // 'raised' sits on the page background; 'flat' sits inside a card
  variant?: 'raised' | 'flat';
  title?: string;
}

const INDICATOR_ICON = { desc: 'sortDesc', asc: 'sortAsc', filter: 'filter' } as const;
const INDICATOR_LABEL = { desc: 'ממוין מהגבוה לנמוך', asc: 'ממוין מהנמוך לגבוה', filter: 'מסונן' };

export const StatTile = ({ value, label, onClick, to, state, selected = false, indicator, variant = 'raised', title }: StatTileProps) => {
  const idleBg = variant === 'flat' ? color.surfaceMuted : color.surface;
  const style: React.CSSProperties = {
    flex: '1 1 64px',
    minWidth: 0,
    minHeight: 60,
    padding: `${space.sm + 2}px ${space.xs + 2}px`,
    borderRadius: radius.lg,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: space.xxs,
    background: selected ? color.primary : idleBg,
    boxShadow: selected ? shadow.selected : variant === 'raised' ? shadow.raised : 'none',
    transform: selected ? 'translateY(-1px)' : 'none',
    transition: 'background 0.15s, transform 0.15s, box-shadow 0.15s',
  };
  const content = (
    <>
      <span style={{ display: 'flex', alignItems: 'center', gap: space.xxs, fontSize: font.heading + 2, fontWeight: weight.bold, lineHeight: 1.15, color: selected ? color.textOnPrimary : color.primary }}>
        {value}
        {selected && indicator && <Icon name={INDICATOR_ICON[indicator]} size={14} strokeWidth={2.4} />}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: space.xxs, fontSize: font.caption, lineHeight: 1.3, color: selected ? color.textOnPrimary : to ? color.primary : color.textSecondary, fontWeight: to ? weight.medium : undefined }}>
        {label}
        {to && <Icon name="chevronForward" size={12} strokeWidth={2.4} />}
      </span>
      {selected && indicator && <span style={visuallyHidden}>{INDICATOR_LABEL[indicator]}</span>}
    </>
  );

  if (to) return <Link to={to} state={state} title={title} style={{ ...style, color: 'inherit', textDecoration: 'none' }}>{content}</Link>;
  if (!onClick) return <div style={style} title={title}>{content}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      title={title}
      style={{ ...buttonReset, ...style }}
    >
      {content}
    </button>
  );
};

// Tiles wrap onto more rows as needed and share each row's width evenly, or sit in a
// fixed number of equal columns
export const StatTileRow = ({ children, columns, style }: { children: React.ReactNode; columns?: number; style?: React.CSSProperties }) => (
  <div style={{
    display: columns ? 'grid' : 'flex',
    gridTemplateColumns: columns ? `repeat(${columns}, minmax(0, 1fr))` : undefined,
    flexWrap: columns ? undefined : 'wrap',
    gap: space.sm,
    ...style,
  }}>
    {children}
  </div>
);

export default StatTile;
